import React, { useEffect, useRef, useState } from "react";
import { X, Send, Bot } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

export default function AiChatModal({ open, onClose }) {
  const { profile } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && profile) {
      api.getAiHistory(profile.id).then(setMessages).catch(() => setMessages([]));
    }
  }, [open, profile]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  if (!open) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await api.chatAi(profile.id, text);
      setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble responding right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-lg relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ height: "640px" }}>
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-900/40 border border-purple-500/50 flex items-center justify-center text-purple-400"><Bot size={20} /></div>
            <div>
              <h3 className="text-base font-bold text-white">DONDRÁE Core</h3>
              <p className="text-[11px] text-purple-400 flex items-center"><span className="w-1.5 h-1.5 rounded-full bg-purple-400 mr-1.5 animate-pulse" /> AI Agent · Online</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 hide-scrollbar">
          {messages.length === 0 && !loading && (
            <div className="text-center text-zinc-500 text-sm mt-10">
              <div className="w-14 h-14 rounded-full bg-purple-900/30 border border-purple-500/30 flex items-center justify-center text-purple-400 mx-auto mb-4"><Bot size={26} /></div>
              <p className="text-white font-bold mb-1">Hi, I'm DONDRÁE Core.</p>
              <p>Ask me about marketing, SEO, events, leads, or growing your business.</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/5 border border-white/10 text-zinc-200 rounded-bl-sm"}`}>{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/10 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="p-4 border-t border-white/10 flex gap-2 bg-black/30">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Message DONDRÁE Core…" className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" />
          <button type="submit" disabled={loading || !input.trim()} className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-xl hover:bg-primary-500 transition disabled:opacity-40"><Send size={18} /></button>
        </form>
      </div>
    </div>
  );
}
